import React, { useEffect, useState } from 'react';

import {
  ICalendarEventType,
  IMatchingAvailabilityChildren,
  PermissionsOfAdmin,
  PharmacistLegendVariant,
  adminActions,
  getMatchingAvailabilities,
  resetStatus,
  selectMatchingAvailabilities,
  successSelector,
  userPreferredTimeFormat,
} from '@pharmaplan/common';

import MultiDayAvailabilityModal from '../../components/Admin/MatchingAvailabilities/MultiDayAvailabilityModal';
import {
  tableHeaderConfig,
  tableRowConfig,
} from '../../components/Admin/MatchingAvailabilities/helper';
import {
  IFromScreen,
  renderScreen,
  setBack,
  setFooterShow,
} from '../../actions/drawerActions';
import { OtherScreens, ScreenTypes } from '../../helpers/Constants';
import useDeleteAvailability from '../Admin/useDeleteAvailability';
import useAdminPermissions from '../Admin/useAdminPermissions';
import { setDialog } from '../../reducers/dialogReducer';
import { useAppDispatch } from '../useAppDispatch';
import { useAppSelector } from '../useAppSelector';
import useSelfService from '../useSelfService';
import strings from '../../localization';
import { showSuccess } from '../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/helper';

const { requested } = PharmacistLegendVariant;
const { availableWorkshift, requestedList } = ScreenTypes;
const deleteSuccessAction = adminActions.deletePharmacistAvailabilities;

export interface IMatchingAvailabilities {
  workshiftId: string;
  type: ICalendarEventType;
  fromScreen?: IFromScreen;
  date: string;
}

const noActionsScreens = [OtherScreens.AdminPostedWorkshiftReports];

const useMatchingAvailabilities = ({
  workshiftId,
  type,
  fromScreen,
  date,
}: IMatchingAvailabilities) => {
  const { isSelfService } = useSelfService();
  const { can } = useAdminPermissions();
  const [showOtherAvailabilities, setShowOtherAvailabilities] = useState(true);
  const dispatch = useAppDispatch();
  const { showDeletePreSubmit } = useDeleteAvailability();

  const matchingAvailabilities = useAppSelector(selectMatchingAvailabilities);
  const timeFormat = useAppSelector(userPreferredTimeFormat);

  const deleteSuccess = useAppSelector((state) =>
    successSelector([deleteSuccessAction], state));

  const isRequested = type === requested;
  const canMakeBooking = can(PermissionsOfAdmin.Booking) || !isSelfService;
  const {
    matchingAvailabilities: matchingData,
    startDate,
    longTermShiftContract,
  } = matchingAvailabilities ?? {};

  const isLongTerm = !!longTermShiftContract?.longTermShiftContractId;

  const noActionsScreen = noActionsScreens.includes(fromScreen as OtherScreens);

  const openModal = (
    children: Array<IMatchingAvailabilityChildren>,
    name: string,
    title: string,
  ) =>
    () => {
      dispatch(
        setDialog({
          Component: <MultiDayAvailabilityModal name={name} data={children} />,
          heading: {
            title: `${strings.matchingAvailabilities} (${title})`,
          },
          customStyle: {
            customModalHeading: {
              fontSize: 14,
            },
          },
          maxWidth: 'sm',
          showCloseButton: true,
        }),
      );
    };

  const back = () => {
    dispatch(
      renderScreen({
        screenNumber: 4,
        screenType: ScreenTypes.availableWorkshift,
        eventId: workshiftId,
        type,
        date,
      }),
    );
  };

  const table = {
    title: '',
    headerBar: [],
    headers: tableHeaderConfig(isLongTerm),
    rows: tableRowConfig({
      openModal,
      data: matchingData,
      isLongTerm,
      timeFormat,
      dispatch,
      workshiftId,
      type,
      back,
      showDeletePreSubmit,
      canMakeBooking,
    }),
  };

  const goToAvailableWorkshift = () => {
    dispatch(
      renderScreen({
        screenType: availableWorkshift,
        screenNumber: 1,
        date,
      }),
    );
  };

  const goToRequestPharmacist = () => {
    dispatch(
      renderScreen({
        screenType: requestedList,
        screenNumber: 2,
        eventId: workshiftId,
      }),
    );
  };

  useEffect(() => {
    dispatch(getMatchingAvailabilities(workshiftId));

    if (noActionsScreen) {
      dispatch(setBack(null));
      dispatch(setFooterShow(false));
    } else {
      isRequested
        ? dispatch(setBack(goToRequestPharmacist))
        : dispatch(setBack(goToAvailableWorkshift));
    }

    return () => {
      dispatch(setBack(null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, workshiftId]);

  useEffect(() => {
    if (deleteSuccess) {
      dispatch(getMatchingAvailabilities(workshiftId));
      showSuccess(dispatch, strings.recordDeletedSuccessfully);
      dispatch(resetStatus([deleteSuccessAction]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteSuccess]);

  return {
    table,
    showOtherAvailabilities,
    setShowOtherAvailabilities,
  };
};

export default useMatchingAvailabilities;
